import React, { useState, useRef, useEffect } from 'react';
import { loadOrCreateIdentity } from '../lib/identity';
import {
  getProfile, getRemoteProfile, getAllVideos, getUserPins,
  calculateTastemakerScore, isFollowing, saveFollow, removeFollow,
  getFollowing, getFollowers,
} from '../lib/metadata';
import { publishEvent } from '../lib/sync';
import { pubkeyShortHash, asText, sanitizeCSSProperties } from '../lib/sanitize';
import { VideoPlayer } from './VideoPlayer';
import { ShopTab } from './ShopTab';
import { COLORS, SPACING } from '../styles/theme';

interface UserProfileProps {
  userId: string;
  onBack: () => void;
  onMessage?: (userId: string, username: string) => void;
  onViewTag?: (tag: string) => void;
}

export function UserProfile({ userId, onBack, onMessage, onViewTag }: UserProfileProps) {
  const identity = loadOrCreateIdentity();
  const isSelf = identity.userId === userId;
  const profile = isSelf ? getProfile() : getRemoteProfile(userId);

  const [tab, setTab] = useState<'created' | 'curated' | 'shop'>('created');
  const [following, setFollowing] = useState(() => isFollowing(identity.userId, userId));
  const [selectedHash, setSelectedHash] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const allVideos = getAllVideos();
  const created = allVideos.filter(v => v.uploader === userId).sort((a, b) => b.uploadedAt - a.uploadedAt);
  const pinned = getUserPins(userId)
    .map(p => allVideos.find(v => v.hash === p.videoHash))
    .filter(Boolean) as typeof allVideos;
  const tastemakerScore = calculateTastemakerScore(userId);
  const followerCount = getFollowers(userId).length;
  const followingCount = getFollowing(userId).length;

  const safeCSS = sanitizeCSSProperties(profile?.cssProperties || {});
  const accentColor = safeCSS['--color-accent'] || profile?.accentColor || COLORS.accent;

  useEffect(() => {
    if (!containerRef.current) return;
    Object.entries(safeCSS).forEach(([k, v]) => containerRef.current!.style.setProperty(k, v));
  }, []);

  async function handleFollow() {
    if (following) {
      removeFollow(identity.userId, userId);
      await publishEvent('unfollow', { followerUserId: identity.userId, followeeUserId: userId } as any);
      setFollowing(false);
    } else {
      saveFollow(identity.userId, userId);
      await publishEvent('follow', { followerUserId: identity.userId, followeeUserId: userId } as any);
      setFollowing(true);
    }
  }

  if (selectedHash) {
    return <VideoPlayer videoHash={selectedHash} onBack={() => setSelectedHash(null)} onViewUserProfile={() => {}} onViewTag={onViewTag} />;
  }

  const contentList = tab === 'created' ? created : pinned;

  return (
    <div ref={containerRef} style={{ padding: SPACING.lg, maxWidth: 600, fontFamily: 'monospace' }}>
      <button onClick={onBack} style={{ background: 'transparent', border: 'none', color: COLORS.textMuted, cursor: 'pointer', fontSize: '12px', marginBottom: SPACING.md }}>
        ← Back
      </button>

      {/* Header */}
      <div style={{ display: 'flex', gap: SPACING.lg, marginBottom: SPACING.lg, alignItems: 'flex-start' }}>
        <div style={{
          width: 64, height: 64, flexShrink: 0,
          border: `2px solid ${accentColor}`,
          borderRadius: profile?.avatarShape === 'circle' ? '50%' : 0,
          background: COLORS.border,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '20px', color: COLORS.textMuted,
        }}>
          {(profile?.username || userId).slice(0, 2).toUpperCase()}
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: SPACING.sm, flexWrap: 'wrap', marginBottom: 4 }}>
            <span style={{ fontWeight: 'bold', color: COLORS.text, fontSize: '16px' }}>
              {asText(profile?.username || userId)}
            </span>
            <span style={{ fontSize: '10px', color: COLORS.textMuted }}>
              [{pubkeyShortHash(profile?.publicKey || '')}]
            </span>
          </div>

          {profile?.bio && (
            <p style={{
              fontFamily: profile.bioFont === 'sans-serif' ? 'sans-serif' : 'monospace',
              fontSize: '12px', color: COLORS.text, margin: `${SPACING.xs} 0`,
            }}>
              {asText(profile.bio)}
            </p>
          )}

          {profile?.links && profile.links.length > 0 && (
            <div style={{ display: 'flex', gap: SPACING.sm, flexWrap: 'wrap', marginBottom: SPACING.xs }}>
              {profile.links.slice(0, 5).map((link, i) => (
                <span key={i} style={{ fontSize: '11px', color: accentColor }}>
                  {asText(link.label || link.url)}
                </span>
              ))}
            </div>
          )}

          <div style={{ fontSize: '11px', color: COLORS.textMuted, display: 'flex', gap: SPACING.md, flexWrap: 'wrap' }}>
            <span>★ {tastemakerScore} tastemaker</span>
            {profile?.socialGraphPublic && (
              <>
                <span>{followerCount} followers</span>
                <span>{followingCount} following</span>
              </>
            )}
          </div>
        </div>

        {!isSelf && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING.xs, flexShrink: 0 }}>
            <button
              onClick={handleFollow}
              style={{
                background: following ? 'transparent' : accentColor,
                color: following ? accentColor : COLORS.bg,
                border: `2px solid ${accentColor}`,
                padding: `${SPACING.xs} ${SPACING.sm}`,
                cursor: 'pointer', fontWeight: 'bold', fontSize: '12px',
              }}
            >
              {following ? 'Following' : 'Follow'}
            </button>
            {onMessage && (
              <button
                onClick={() => onMessage(userId, profile?.username ?? userId)}
                style={{
                  background: 'transparent',
                  color: accentColor,
                  border: `1px solid ${accentColor}`,
                  padding: `${SPACING.xs} ${SPACING.sm}`,
                  cursor: 'pointer', fontSize: '11px',
                  fontFamily: 'monospace',
                }}
              >
                Message
              </button>
            )}
          </div>
        )}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: SPACING.sm, borderBottom: `1px solid ${COLORS.border}`, marginBottom: SPACING.md }}>
        {(['created', 'curated', 'shop'] as const).map(t => (
          <button
            key={t}
            onClick={() => setTab(t)}
            style={{
              background: 'transparent', border: 'none',
              borderBottom: tab === t ? `2px solid ${accentColor}` : '2px solid transparent',
              color: tab === t ? COLORS.text : COLORS.textMuted,
              padding: `${SPACING.xs} ${SPACING.sm}`,
              cursor: 'pointer', fontSize: '12px', fontWeight: tab === t ? 'bold' : 'normal',
            }}
          >
            {t === 'created' ? '▶ Created' : t === 'curated' ? `★ Curated · ${tastemakerScore}` : 'Shop'}
          </button>
        ))}
      </div>

      {/* Content */}
      {tab === 'shop' ? (
        <ShopTab userId={userId} isSelf={isSelf} />
      ) : contentList.length === 0 ? (
        <p style={{ fontSize: '12px', color: COLORS.textMuted }}>
          {tab === 'created' ? 'No videos uploaded yet.' : 'No videos pinned yet.'}
        </p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING.sm }}>
          {contentList.map((video: any) => (
            <div
              key={video.hash}
              onClick={() => setSelectedHash(video.hash)}
              style={{ border: `1px solid ${COLORS.border}`, padding: SPACING.sm, cursor: 'pointer' }}
            >
              <div style={{ fontSize: '13px', color: COLORS.text, fontWeight: 'bold' }}>
                {asText(video.title)}
              </div>
              <div style={{ fontSize: '11px', color: COLORS.textMuted }}>
                {new Date(video.uploadedAt).toLocaleDateString()}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
